import Popover from '@ui/popover/popover';
import Container from '@ui/container/container';
import Button from '@ui/buttons/button/button';
import Text from '@ui/text/text/text';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import { generateBorderDashSvg } from '@/micros/edit-project/helpers/constants/border-constants';
import { VideoType } from '@/types/video.types';
import { useState, useEffect } from 'react';

type TAddVideoPopoverProps = {
  isUpdatingBroadcastType: boolean;
  autoOpen?: boolean;
  onSelect: (selectedOption: VideoType | null) => void;
};

const AddVideoPopover = ({ isUpdatingBroadcastType, autoOpen = false, onSelect }: TAddVideoPopoverProps) => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (autoOpen) setIsOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelect = (opt: VideoType | null) => {
    setIsOpen(false);
    onSelect(opt);
  };

  return (
    <Container className="flex-1" borderRadius="8px" background="var(--global-cont-1)">
      <Popover
        open={isOpen}
        onClose={() => setIsOpen(false)}
        cycleWithArrowKeys
        backgroundColor="var(--global-cont-3)"
        contentClassName="margin-t-4"
        radius="12px"
        inheritRadius
        width="auto"
        renderTriggerButton={() => {
          return (
            <Container height="84px" width="100%" className="flex ai-center jc-center padding-12">
              <Button
                width="100%"
                height="100%"
                disabled={isUpdatingBroadcastType}
                style={{
                  background: `url(${generateBorderDashSvg('#9B97A1', 8)})`,
                }}
                borderRadius="8px"
                className="flex ai-center jc-center gap-6"
                onClick={() => setIsOpen((prev) => !prev)}
              >
                <Icon name={ICONS.ADD} size={12} />
                <Text className="L2" design="GlobalIcoText1">
                  Add Video
                </Text>
              </Button>
            </Container>
          );
        }}
      >
        <Button
          backgroundColor="var(--global-cont-2)"
          hoverBackgroundColor="var(--global-cont-3)"
          outlineOnFocus={false}
          className="flex ai-center gap-10 radius-0 width-100-percent height-60 padding-v-8 padding-h-12"
          onClick={() => handleSelect(VideoType.recorded)}
        >
          <Container className="width-18">
            <Icon name={ICONS.ADD} size={20} />
          </Container>
          <Container className="flex-column">
            <Text className="P2" design="GlobalIcoText1">
              Add Video
            </Text>
            <Text className="L2" design="GlobalIcoText3">
              Add a video from your channel content or upload a file up to 5 GB
            </Text>
          </Container>
        </Button>
        <Button
          backgroundColor="var(--global-cont-2)"
          hoverBackgroundColor="var(--global-cont-3)"
          outlineOnFocus={false}
          className="flex ai-center gap-10 radius-0 width-100-percent height-60 padding-v-8 padding-h-12"
          onClick={() => handleSelect(VideoType.url)}
        >
          <Container className="width-18">
            <Icon name={ICONS.CHAIN_LINK} size={20} />
          </Container>
          <Container className="flex-column">
            <Text className="P2" design="GlobalIcoText1">
              Link Video
            </Text>
            <Text className="L2" design="GlobalIcoText3">
              Use YouTube, Vimeo or Wistia link
            </Text>
          </Container>
        </Button>
        <Button
          backgroundColor="var(--global-cont-2)"
          hoverBackgroundColor="var(--global-cont-3)"
          outlineOnFocus={false}
          className="flex ai-center gap-10 radius-0 width-100-percent height-60 padding-v-8 padding-h-12"
          onClick={() => handleSelect(VideoType.embed)}
        >
          <Container className="width-18">
            <Icon name={ICONS.EMBED} size={20} />
          </Container>
          <Container className="flex-column">
            <Text className="P2" design="GlobalIcoText1">
              Embed iframe
            </Text>
            <Text className="L2" design="GlobalIcoText3">
              Add video via HTML embed code
            </Text>
          </Container>
        </Button>
      </Popover>
    </Container>
  );
};

export default AddVideoPopover;
